import React from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";

import { Button as UiButton } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import {
  GenerateReportWizardStepper,
  GenerateReportWizardStepperContent,
} from "../Reports/GenerateReportWizardStepper";
import { useWorkspaceWizardFooterSlot } from "../Workspace/WorkspaceWizardDialog";
import GradientButton from "../Profile/components/GradientButton";
import { SimulatorBackNav } from "./SimulatorBackNav";
import { SimulatorAlert } from "./SimulatorAlert";
import { reportPageFieldFocusSx } from "../Reports/reportPageFieldFocus";
import { SIMULATOR_RUN_ACCENT, SIMULATOR_RUN_STEPS } from "./simulatorRunWizardConstants";
import { SimulatorRunWizardStepsHeader } from "./SimulatorRunWizard";

const CANCEL_RED = "#E00101";
const MENU_RADIUS = "6px";

export type SimulatorRunWizardLayoutProps = {
  subtleText: string;
  borderColor: string;
  cardBg: string;
  cardShadow: string;
  step: number;
  onStepClick: (index: number) => void;
  onExit: () => void;
  onCancel: () => void;
  onFooterBack: () => void;
  onContinue: () => void;
  continueDisabled: boolean;
  /** When set on the policy step, a problem worth surfacing (e.g. missing catalog or invalid policy), not routine empty fields. */
  continueBlockedReason?: string | null;
  onGenerate: () => void;
  /** Trusted pointer-down on Generate — arms the canvas Edit & run gesture gate. */
  onGeneratePointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  generating: boolean;
  generateDisabled: boolean;
  /** Dialog embed — match strategy / report create wizard chrome. */
  embedded?: boolean;
  children: React.ReactNode;
};

function SimulatorRunWizardDialogBody(props: {
  step: number;
  onStepClick: (index: number) => void;
  onCancel: () => void;
  onFooterBack: () => void;
  onContinue: () => void;
  continueDisabled: boolean;
  continueBlockedReason?: string | null;
  onGenerate: () => void;
  onGeneratePointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  generating: boolean;
  generateDisabled: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const footerSlot = useWorkspaceWizardFooterSlot();
  const lastStepIndex = SIMULATOR_RUN_STEPS.length - 1;

  const footer = (
    <DialogFooter
      data-simulator-run-wizard-footer=""
      showCloseButton={false}
      className="mx-0 mb-0 mt-0 w-full shrink-0 justify-end gap-3 rounded-b-[6px] border-t border-border bg-muted px-6 py-4 sm:flex-row sm:justify-end"
    >
      {props.step > 0 ? (
        <UiButton
          type="button"
          variant="outline-gray"
          size="sm"
          data-font="primary"
          className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
          onClick={props.onFooterBack}
          disabled={props.generating}
        >
          <span data-font="primary" className="truncate normal-case tracking-normal">
            Back
          </span>
        </UiButton>
      ) : null}
      {/* Keep both actions mounted; hide the inactive one with `hidden` (not
          `invisible absolute` — that left a gradient scrap between Back and
          Generate while Running…). Swapping the same slot lets one click fire both. */}
      <UiButton
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className={
          props.step < lastStepIndex
            ? "h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
            : "hidden h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
        }
        tabIndex={props.step < lastStepIndex ? 0 : -1}
        aria-hidden={props.step >= lastStepIndex}
        onClick={props.onContinue}
        disabled={props.continueDisabled || props.generating || props.step >= lastStepIndex}
      >
        <span className="truncate normal-case tracking-normal">Continue</span>
      </UiButton>
      <UiButton
        type="button"
        variant="gradient"
        size="sm"
        data-font="primary"
        className={
          props.step >= lastStepIndex
            ? "h-8 shrink-0 gap-1.5 px-5 text-[0.75rem] normal-case tracking-normal"
            : "hidden h-8 shrink-0 gap-1.5 px-5 text-[0.75rem] normal-case tracking-normal"
        }
        tabIndex={props.step >= lastStepIndex ? 0 : -1}
        aria-hidden={props.step < lastStepIndex}
        onPointerDown={props.onGeneratePointerDown}
        onClick={props.onGenerate}
        disabled={props.generateDisabled || props.generating || props.step < lastStepIndex}
      >
        {props.generating ? (
          <>
            <Loader2 className="size-4 shrink-0 animate-spin" aria-hidden />
            <span className="truncate normal-case tracking-normal">Running…</span>
          </>
        ) : (
          <span className="truncate normal-case tracking-normal">Generate simulation</span>
        )}
      </UiButton>
    </DialogFooter>
  );

  return (
    <>
      {footerSlot ? createPortal(footer, footerSlot) : null}
      <div
        data-simulator-run-wizard-dialog=""
        className="flex w-full flex-col px-6 py-4"
      >
        <GenerateReportWizardStepper
          activeStepIndex={props.step}
          onActiveStepIndexChange={props.onStepClick}
          stepCount={SIMULATOR_RUN_STEPS.length}
          compact
        >
          {SIMULATOR_RUN_STEPS.map((step, index) => (
            <GenerateReportWizardStepperContent key={step.key} step={index + 1}>
              {step.key !== "generate" ? (
                <div className="mb-4">
                  <p className="m-0 font-[family-name:var(--font-primary)] text-sm font-normal text-[color:var(--brand-tropical-surf)]">
                    {step.label}
                  </p>
                  <p className="mt-1 m-0 font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white">
                    {step.description}
                  </p>
                </div>
              ) : null}
              {props.children}
              {props.step === 1 && index === 1 && props.continueBlockedReason ? (
                <div className="mt-4">
                  <SimulatorAlert tone="warning">{props.continueBlockedReason}</SimulatorAlert>
                </div>
              ) : null}
            </GenerateReportWizardStepperContent>
          ))}
        </GenerateReportWizardStepper>
      </div>
    </>
  );
}

export function SimulatorRunWizardLayout(props: SimulatorRunWizardLayoutProps): React.ReactElement {
  const meta = SIMULATOR_RUN_STEPS[props.step] ?? SIMULATOR_RUN_STEPS[0];
  const accent = SIMULATOR_RUN_ACCENT;
  const embedded = Boolean(props.embedded);

  if (embedded) {
    return (
      <SimulatorRunWizardDialogBody
        step={props.step}
        onStepClick={props.onStepClick}
        onCancel={props.onCancel}
        onFooterBack={props.onFooterBack}
        onContinue={props.onContinue}
        continueDisabled={props.continueDisabled}
        continueBlockedReason={props.continueBlockedReason}
        onGenerate={props.onGenerate}
        onGeneratePointerDown={props.onGeneratePointerDown}
        generating={props.generating}
        generateDisabled={props.generateDisabled}
      >
        {props.children}
      </SimulatorRunWizardDialogBody>
    );
  }

  return (
    <Flex
      direction="column"
      minH={{ base: "calc(100vh - 100px)", md: "calc(100vh - 80px)", lg: "calc(100vh - 40px)" }}
      w="100%"
      sx={reportPageFieldFocusSx}
    >
      <Box flex="1" w="100%" maxW="1120px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <SimulatorBackNav label="Back to simulator" subtleText={props.subtleText} onClick={props.onExit} />

        <Box mb={{ base: 5, md: 6 }}>
          <Text
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            color={accent}
            fontFamily="'Urbanist', sans-serif"
            fontWeight="normal"
            lineHeight="1.1"
            mb={2}
          >
            New simulation
          </Text>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color={props.subtleText}
            fontFamily="'IBM Plex Sans', sans-serif"
            maxW="640px"
            lineHeight="1.5"
          >
            Walk through scope, build your policy, then review and generate your simulation run.
          </Text>
        </Box>

        <SimulatorRunWizardStepsHeader
          step={props.step}
          subtleText={props.subtleText}
          borderColor={props.borderColor}
          cardBg={props.cardBg}
          cardShadow={props.cardShadow}
          onStepClick={props.onStepClick}
        />

        <Box
          bg={props.cardBg}
          borderWidth="1px"
          borderColor={props.borderColor}
          borderRadius="16px"
          p={{ base: 5, md: 8 }}
          boxShadow={props.cardShadow}
          w="100%"
          overflow="visible"
        >
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            color={accent}
            fontFamily="'Urbanist', sans-serif"
            fontWeight="semibold"
            lineHeight="1.2"
            mb={1}
          >
            {meta.label}
          </Text>
          <Text fontSize="sm" color={props.subtleText} fontFamily="'IBM Plex Sans', sans-serif" mb={6} lineHeight="1.45">
            {meta.description}
          </Text>
          {props.children}
        </Box>

        {props.step === 1 && props.continueBlockedReason ? (
          <SimulatorAlert tone="warning" className="mt-4">
            {props.continueBlockedReason}
          </SimulatorAlert>
        ) : null}

        <Flex w="100%" justify="space-between" align="center" gap={3} flexWrap="wrap" mt={4}>
          <Button
            variant="outline"
            h="40px"
            px="16px"
            bg="transparent"
            borderColor={CANCEL_RED}
            color={CANCEL_RED}
            fontFamily="'IBM Plex Sans', sans-serif"
            fontSize="xs"
            fontWeight="medium"
            textTransform="uppercase"
            borderRadius={MENU_RADIUS}
            _hover={{
              borderColor: CANCEL_RED,
              color: CANCEL_RED,
              opacity: 0.88,
              bg: "rgba(224, 1, 1, 0.06)",
            }}
            _active={{ opacity: 0.8 }}
            onClick={props.onCancel}
          >
            Cancel
          </Button>
          <HStack spacing={3}>
            {props.step > 0 ? (
              <Button
                variant="outline"
                h="40px"
                px="16px"
                borderColor={props.borderColor}
                color={props.subtleText}
                fontFamily="'IBM Plex Sans', sans-serif"
                fontSize="xs"
                fontWeight="medium"
                textTransform="uppercase"
                borderRadius={MENU_RADIUS}
                onClick={props.onFooterBack}
              >
                Back
              </Button>
            ) : null}
            {props.step < 2 ? (
              <GradientButton
                showArrow={false}
                fontSize="xs"
                h="40px"
                px="20px"
                py="10px"
                onClick={props.onContinue}
                isDisabled={props.continueDisabled}
              >
                Continue
              </GradientButton>
            ) : (
              <GradientButton
                showArrow={false}
                fontSize="xs"
                h="40px"
                px="20px"
                py="10px"
                isLoading={props.generating}
                loadingText="Running…"
                onClick={props.onGenerate}
                isDisabled={props.generateDisabled}
              >
                Generate simulation
              </GradientButton>
            )}
          </HStack>
        </Flex>
      </Box>
    </Flex>
  );
}
